import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Widget {
  id: string;
  type:
    | 'kpi'
    | 'chart'
    | 'list'
    | 'counter'
    | 'progress'
    | 'alert'
    | 'quickaction';
  title: string;
  subtitle?: string;
  size: 'small' | 'medium' | 'large' | 'xlarge';
  position: { x: number; y: number };
  color: string;
  icon?: string;
  data: any;
  config: WidgetConfig;
  isVisible: boolean;
  isMovable: boolean;
  isResizable: boolean;
  isEditable: boolean;
  updatedAt: number;
  refreshInterval?: number; // in seconds
}

export interface WidgetConfig {
  showTitle: boolean;
  showSubtitle: boolean;
  showIcon: boolean;
  showBorder: boolean;
  showShadow: boolean;
  roundedCorners: boolean;
  transparentBackground: boolean;
  customCss?: string;
  dataSource?: string;
  filters?: any;
  sortBy?: string;
  limit?: number;
}

export interface WidgetTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  type: Widget['type'];
  defaultSize: Widget['size'];
  defaultColor: string;
  icon: string;
  previewImage: string;
  dataRequirement: string[];
  configOptions: any;
}

@Injectable({
  providedIn: 'root',
})
export class WidgetService {
  private widgetsSubject = new BehaviorSubject<Widget[]>([]);
  private templatesSubject = new BehaviorSubject<WidgetTemplate[]>([]);
  private currentLayoutSubject = new BehaviorSubject<string>('default');

  public widgets$ = this.widgetsSubject.asObservable();
  public templates$ = this.templatesSubject.asObservable();
  public currentLayout$ = this.currentLayoutSubject.asObservable();

  constructor() {
    this.initializeDefaultTemplates();
    this.loadWidgets();
    this.setupAutoRefresh();
  }

  private initializeDefaultTemplates(): void {
    const templates: WidgetTemplate[] = [
      {
        id: 'sales-today',
        name: 'Vendas Hoje',
        description: 'KPI de vendas do dia atual',
        category: 'Vendas',
        type: 'kpi',
        defaultSize: 'medium',
        defaultColor: '#3B82F6',
        icon: 'trending_up',
        previewImage: '/assets/widgets/sales-today.png',
        dataRequirement: ['sales'],
        configOptions: {
          showComparison: true,
          showChart: true,
          period: 'today',
        },
      },
      {
        id: 'stock-alerts',
        name: 'Alertas de Estoque',
        description: 'Lista de produtos com estoque baixo',
        category: 'Estoque',
        type: 'alert',
        defaultSize: 'large',
        defaultColor: '#EF4444',
        icon: 'warning',
        previewImage: '/assets/widgets/stock-alerts.png',
        dataRequirement: ['inventory'],
        configOptions: {
          threshold: 10,
          showQuantity: true,
          sortBy: 'quantity',
        },
      },
      {
        id: 'top-products',
        name: 'Top Produtos',
        description: 'Produtos mais vendidos',
        category: 'Produtos',
        type: 'chart',
        defaultSize: 'large',
        defaultColor: '#10B981',
        icon: 'star',
        previewImage: '/assets/widgets/top-products.png',
        dataRequirement: ['sales', 'products'],
        configOptions: {
          chartType: 'bar',
          period: 'week',
          limit: 5,
        },
      },
      {
        id: 'quick-sale',
        name: 'Venda Rápida',
        description: 'Botão para iniciar venda rapidamente',
        category: 'Ações',
        type: 'quickaction',
        defaultSize: 'small',
        defaultColor: '#8B5CF6',
        icon: 'point_of_sale',
        previewImage: '/assets/widgets/quick-sale.png',
        dataRequirement: [],
        configOptions: {
          action: 'navigate',
          target: '/pdv',
          label: 'Iniciar Venda',
        },
      },
      {
        id: 'revenue-goal',
        name: 'Meta de Faturamento',
        description: 'Progresso da meta mensal',
        category: 'Metas',
        type: 'progress',
        defaultSize: 'medium',
        defaultColor: '#F59E0B',
        icon: 'flag',
        previewImage: '/assets/widgets/revenue-goal.png',
        dataRequirement: ['sales'],
        configOptions: {
          goalAmount: 50000,
          period: 'month',
          showPercentage: true,
        },
      },
      {
        id: 'customer-counter',
        name: 'Total de Clientes',
        description: 'Contador de clientes cadastrados',
        category: 'Clientes',
        type: 'counter',
        defaultSize: 'small',
        defaultColor: '#06B6D4',
        icon: 'people',
        previewImage: '/assets/widgets/customer-counter.png',
        dataRequirement: ['customers'],
        configOptions: {
          showGrowth: true,
          period: 'month',
        },
      },
      {
        id: 'recent-orders',
        name: 'Pedidos Recentes',
        description: 'Lista dos últimos pedidos',
        category: 'Pedidos',
        type: 'list',
        defaultSize: 'large',
        defaultColor: '#EC4899',
        icon: 'receipt_long',
        previewImage: '/assets/widgets/recent-orders.png',
        dataRequirement: ['orders'],
        configOptions: {
          limit: 5,
          showStatus: true,
          showAmount: true,
        },
      },
      {
        id: 'sales-chart',
        name: 'Gráfico de Vendas',
        description: 'Gráfico de tendência de vendas',
        category: 'Vendas',
        type: 'chart',
        defaultSize: 'xlarge',
        defaultColor: '#14B8A6',
        icon: 'show_chart',
        previewImage: '/assets/widgets/sales-chart.png',
        dataRequirement: ['sales'],
        configOptions: {
          chartType: 'line',
          period: 'month',
          showDataPoints: true,
        },
      },
    ];

    this.templatesSubject.next(templates);
  }

  private loadWidgets(): void {
    const stored = localStorage.getItem('estokai-widgets');
    if (stored) {
      try {
        const widgets = JSON.parse(stored);
        this.widgetsSubject.next(widgets);
      } catch (error) {
        console.error('Error loading widgets:', error);
        this.createDefaultWidgets();
      }
    } else {
      this.createDefaultWidgets();
    }
  }

  private createDefaultWidgets(): void {
    const defaultWidgets: Widget[] = [
      {
        id: 'widget-1',
        type: 'kpi',
        title: 'Vendas Hoje',
        subtitle: 'Faturamento do dia',
        size: 'medium',
        position: { x: 0, y: 0 },
        color: '#3B82F6',
        icon: 'trending_up',
        data: {
          value: 2450.0,
          currency: 'BRL',
          change: 12.5,
          changeType: 'increase',
          comparison: 'vs. ontem',
        },
        config: {
          showTitle: true,
          showSubtitle: true,
          showIcon: true,
          showBorder: false,
          showShadow: true,
          roundedCorners: true,
          transparentBackground: false,
        },
        isVisible: true,
        isMovable: true,
        isResizable: true,
        isEditable: true,
        updatedAt: Date.now(),
      },
      {
        id: 'widget-2',
        type: 'alert',
        title: 'Alertas de Estoque',
        size: 'large',
        position: { x: 1, y: 0 },
        color: '#EF4444',
        icon: 'warning',
        data: {
          alerts: [
            { product: 'Produto A', quantity: 5, threshold: 10 },
            { product: 'Produto B', quantity: 2, threshold: 15 },
            { product: 'Produto C', quantity: 8, threshold: 20 },
          ],
        },
        config: {
          showTitle: true,
          showSubtitle: false,
          showIcon: true,
          showBorder: false,
          showShadow: true,
          roundedCorners: true,
          transparentBackground: false,
        },
        isVisible: true,
        isMovable: true,
        isResizable: true,
        isEditable: true,
        updatedAt: Date.now(),
      },
      {
        id: 'widget-3',
        type: 'quickaction',
        title: 'Venda Rápida',
        size: 'small',
        position: { x: 2, y: 0 },
        color: '#8B5CF6',
        icon: 'point_of_sale',
        data: {
          action: 'navigate',
          target: '/pdv',
          label: 'Iniciar PDV',
        },
        config: {
          showTitle: true,
          showSubtitle: false,
          showIcon: true,
          showBorder: false,
          showShadow: true,
          roundedCorners: true,
          transparentBackground: false,
        },
        isVisible: true,
        isMovable: true,
        isResizable: true,
        isEditable: true,
        updatedAt: Date.now(),
      },
      {
        id: 'widget-4',
        type: 'progress',
        title: 'Meta Mensal',
        subtitle: 'R$ 50.000,00',
        size: 'medium',
        position: { x: 0, y: 1 },
        color: '#F59E0B',
        icon: 'flag',
        data: {
          current: 32500,
          target: 50000,
          percentage: 65,
          remaining: 17500,
          daysLeft: 12,
        },
        config: {
          showTitle: true,
          showSubtitle: true,
          showIcon: true,
          showBorder: false,
          showShadow: true,
          roundedCorners: true,
          transparentBackground: false,
        },
        isVisible: true,
        isMovable: true,
        isResizable: true,
        isEditable: true,
        updatedAt: Date.now(),
      },
      {
        id: 'widget-5',
        type: 'chart',
        title: 'Vendas da Semana',
        size: 'xlarge',
        position: { x: 1, y: 1 },
        color: '#14B8A6',
        icon: 'show_chart',
        data: {
          chartType: 'line',
          data: [
            { day: 'Seg', value: 1200 },
            { day: 'Ter', value: 1800 },
            { day: 'Qua', value: 1500 },
            { day: 'Qui', value: 2200 },
            { day: 'Sex', value: 2800 },
            { day: 'Sab', value: 3200 },
            { day: 'Dom', value: 2100 },
          ],
        },
        config: {
          showTitle: true,
          showSubtitle: false,
          showIcon: true,
          showBorder: false,
          showShadow: true,
          roundedCorners: true,
          transparentBackground: false,
        },
        isVisible: true,
        isMovable: true,
        isResizable: true,
        isEditable: true,
        updatedAt: Date.now(),
      },
    ];

    this.widgetsSubject.next(defaultWidgets);
    this.saveWidgets(defaultWidgets);
  }

  private setupAutoRefresh(): void {
    // Refresh widgets every 30 seconds
    setInterval(() => {
      this.refreshAllWidgets();
    }, 30000);
  }

  public getWidgets(): Widget[] {
    return this.widgetsSubject.value;
  }

  public getWidget(id: string): Widget | undefined {
    return this.widgetsSubject.value.find((w) => w.id === id);
  }

  public addWidget(
    template: WidgetTemplate,
    position?: { x: number; y: number }
  ): Widget {
    const widget: Widget = {
      id: this.generateId(),
      type: template.type,
      title: template.name,
      size: template.defaultSize,
      position: position || this.findEmptyPosition(),
      color: template.defaultColor,
      icon: template.icon,
      data: this.generateMockData(template.type),
      config: {
        showTitle: true,
        showSubtitle: true,
        showIcon: true,
        showBorder: false,
        showShadow: true,
        roundedCorners: true,
        transparentBackground: false,
        ...template.configOptions,
      },
      isVisible: true,
      isMovable: true,
      isResizable: true,
      isEditable: true,
      updatedAt: Date.now(),
    };

    const widgets = [...this.widgetsSubject.value, widget];
    this.widgetsSubject.next(widgets);
    this.saveWidgets(widgets);

    return widget;
  }

  public updateWidget(id: string, updates: Partial<Widget>): void {
    const widgets = this.widgetsSubject.value.map((w) =>
      w.id === id ? { ...w, ...updates, updatedAt: Date.now() } : w
    );

    this.widgetsSubject.next(widgets);
    this.saveWidgets(widgets);
  }

  public deleteWidget(id: string): void {
    const widgets = this.widgetsSubject.value.filter((w) => w.id !== id);
    this.widgetsSubject.next(widgets);
    this.saveWidgets(widgets);
  }

  public moveWidget(id: string, newPosition: { x: number; y: number }): void {
    this.updateWidget(id, { position: newPosition });
  }

  public resizeWidget(id: string, newSize: Widget['size']): void {
    this.updateWidget(id, { size: newSize });
  }

  public toggleWidgetVisibility(id: string): void {
    const widget = this.getWidget(id);
    if (widget) {
      this.updateWidget(id, { isVisible: !widget.isVisible });
    }
  }

  public duplicateWidget(id: string): Widget | null {
    const widget = this.getWidget(id);
    if (!widget) return null;

    const newWidget: Widget = {
      ...widget,
      id: this.generateId(),
      title: `${widget.title} (Cópia)`,
      position: this.findEmptyPosition(),
      updatedAt: Date.now(),
    };

    const widgets = [...this.widgetsSubject.value, newWidget];
    this.widgetsSubject.next(widgets);
    this.saveWidgets(widgets);

    return newWidget;
  }

  public refreshWidget(id: string): void {
    const widget = this.getWidget(id);
    if (widget) {
      const newData = this.generateMockData(widget.type);
      this.updateWidget(id, { data: newData });
    }
  }

  public refreshAllWidgets(): void {
    const widgets = this.widgetsSubject.value.map((widget) => ({
      ...widget,
      data: this.generateMockData(widget.type),
      updatedAt: Date.now(),
    }));

    this.widgetsSubject.next(widgets);
    this.saveWidgets(widgets);
  }

  public exportLayout(): string {
    return JSON.stringify(this.widgetsSubject.value, null, 2);
  }

  public importLayout(layoutJson: string): boolean {
    try {
      const widgets = JSON.parse(layoutJson);
      if (Array.isArray(widgets)) {
        this.widgetsSubject.next(widgets);
        this.saveWidgets(widgets);
        return true;
      }
    } catch (error) {
      console.error('Error importing layout:', error);
    }
    return false;
  }

  public resetToDefault(): void {
    this.createDefaultWidgets();
  }

  private generateId(): string {
    return (
      'widget-' + Date.now().toString(36) + Math.random().toString(36).substr(2)
    );
  }

  private findEmptyPosition(): { x: number; y: number } {
    const widgets = this.widgetsSubject.value;
    const occupiedPositions = widgets.map((w) => w.position);

    for (let y = 0; y < 10; y++) {
      for (let x = 0; x < 4; x++) {
        const position = { x, y };
        if (!occupiedPositions.some((pos) => pos.x === x && pos.y === y)) {
          return position;
        }
      }
    }

    return { x: 0, y: 0 };
  }

  private generateMockData(type: Widget['type']): any {
    switch (type) {
      case 'kpi':
        return {
          value: Math.floor(Math.random() * 10000) + 1000,
          currency: 'BRL',
          change: Math.floor(Math.random() * 30) - 15,
          changeType: Math.random() > 0.5 ? 'increase' : 'decrease',
          comparison: 'vs. período anterior',
        };

      case 'counter':
        return {
          value: Math.floor(Math.random() * 1000) + 100,
          growth: Math.floor(Math.random() * 20) - 10,
          period: 'este mês',
        };

      case 'progress':
        const current = Math.floor(Math.random() * 50000) + 10000;
        const target = 50000;
        return {
          current,
          target,
          percentage: Math.floor((current / target) * 100),
          remaining: target - current,
          daysLeft: Math.floor(Math.random() * 30) + 1,
        };

      case 'chart':
        return {
          chartType: 'line',
          data: Array.from({ length: 7 }, (_, i) => ({
            day: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'][i],
            value: Math.floor(Math.random() * 3000) + 500,
          })),
        };

      case 'alert':
        return {
          alerts: [
            {
              product: 'Produto A',
              quantity: Math.floor(Math.random() * 10) + 1,
              threshold: 15,
            },
            {
              product: 'Produto B',
              quantity: Math.floor(Math.random() * 5) + 1,
              threshold: 10,
            },
            {
              product: 'Produto C',
              quantity: Math.floor(Math.random() * 20) + 1,
              threshold: 25,
            },
          ],
        };

      case 'list':
        return {
          items: Array.from({ length: 5 }, (_, i) => ({
            id: i + 1,
            title: `Item ${i + 1}`,
            subtitle: `Descrição do item ${i + 1}`,
            value: Math.floor(Math.random() * 500) + 50,
            status: ['pending', 'completed', 'cancelled'][
              Math.floor(Math.random() * 3)
            ],
          })),
        };

      case 'quickaction':
        return {
          action: 'navigate',
          target: '/pdv',
          label: 'Ação Rápida',
        };

      default:
        return {};
    }
  }

  private saveWidgets(widgets: Widget[]): void {
    try {
      localStorage.setItem('estokai-widgets', JSON.stringify(widgets));
    } catch (error) {
      console.error('Error saving widgets:', error);
    }
  }
}
